import { useAuthModal } from '@/hooks/useAuthModal'
import posthog from 'posthog-js'
import type { ApiUser } from 'sprites-types/api/users'
import EmailVerification from 'supertokens-web-js/recipe/emailverification'

/**
 * Shared utility for handling email verification requirements
 */

async function isEmailVerificationRequired(
  featureFlags?: Record<string, boolean | string>
): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    if (posthog.__loaded) {
      const userId = posthog.get_distinct_id()
      if (userId && userId !== 'anonymous') {
        const flagValue = posthog.isFeatureEnabled('email_verification_required')
        if (flagValue !== undefined) {
          return flagValue
        }
      }
    }
  } catch (error) {
    console.warn('[EmailVerification] Failed to check feature flag from PostHog:', error)
  }

  if (featureFlags) {
    const flagValue = featureFlags['email_verification_required']
    if (typeof flagValue === 'boolean') {
      return flagValue
    }
  }

  return false
}

export const handleEmailVerificationRequired = async (user?: ApiUser): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false
  }

  const featureFlags = user?.featureFlags
  const isRequired = await isEmailVerificationRequired(featureFlags)
  if (!isRequired) {
    return false
  }

  try {
    const verificationStatus = await EmailVerification.isEmailVerified()

    if (verificationStatus.status === 'OK' && !verificationStatus.isVerified) {
      const authModalStore = useAuthModal.getState()
      const redirectTarget = `${window.location.pathname}${window.location.search}`

      if (!authModalStore.isOpen) {
        authModalStore.onOpen({ redirectUrl: redirectTarget, mode: 'emailVerification' })
      } else {
        authModalStore.setMode('emailVerification')
      }
      return true
    }

    return false
  } catch (error) {
    console.warn('[EmailVerification] Failed to check email verification status:', error)
    return false
  }
}
